import {
  Alert, Badge, Box, Button, Group, Loader, ScrollArea, Stack, Text, TextInput,
  Tooltip, UnstyledButton, useComputedColorScheme,
} from '@mantine/core'
import { useMediaQuery } from '@mantine/hooks'
import {
  IconAlertTriangle, IconEyeOff, IconLayoutSidebarLeftCollapse,
  IconLayoutSidebarLeftExpand, IconPlugConnectedX, IconSearch, IconServer,
  IconPin,
  IconX,
} from '@tabler/icons-react'
import { useQuery } from '@tanstack/react-query'
import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'

import { avisosDeAcesso, useAcessoServidores } from '@/hooks/useAcessoServidores'
import { useCurrentTenant } from '@/store/auth'

import {
  avisoDeCobertura, contarSessoes, estaAlcancavel, explicarOcultos,
  filtrarInstancias, ssmApi, type InstanciaSsm,
} from '../../api/ssm'

import { FUNDO_DO_TERMINAL, TerminalDoServidor } from './TerminalDoServidor'
import { despinar, pinar } from '@/store/ssm'
import { useSessoesSsm, type EstadoDaAba } from './useSessaoSsm'

const COR_DO_AMBIENTE: Record<string, string> = {
  PRD: 'red', HML: 'orange', QA: 'blue', DEV: 'gray',
}

const COR_DO_ESTADO: Record<EstadoDaAba, string> = {
  conectando: 'yellow', aberta: 'teal', encerrada: 'gray', recusada: 'red',
}

export function Ssm() {
  const esquema = useComputedColorScheme('light')
  const tenant = useCurrentTenant()
  const tenantId = tenant?.id
  const [busca, setBusca] = useState('')

  // A tela serve a dois usos: achar um servidor e rodar um comando, ou deixar
  // varias sessoes abertas acompanhando log. Recolher a lista devolve ~280px
  // ao terminal sem fechar sessao nenhuma — o segundo uso sem penalizar o
  // primeiro. Em tela estreita nao existe: la ja se mostra um de cada vez.
  const [recolhida, setRecolhida] = useState(false)

  // Em tela estreita, lateral de 280px + terminal disputam o mesmo espaço e o
  // terminal fica inutilizável. Aqui os dois passam a ocupar a largura toda,
  // um de cada vez: escolhe o servidor, o terminal cobre a tela, e o botão de
  // voltar traz a lista.
  const estreito = useMediaQuery('(max-width: 62em)') ?? false

  const { data, isLoading, error } = useQuery({
    queryKey: ['ssm', 'instancias', tenantId],
    queryFn: () => ssmApi.listarInstancias(tenantId!),
    enabled: !!tenantId,
  })

  // Contagem do teto. `refetchInterval` porque as sessões podem ser abertas de
  // outra aba ou de outro dispositivo — o contador é compartilhado no Redis.
  const { data: contagem } = useQuery({
    queryKey: ['ssm', 'sessoes'],
    queryFn: contarSessoes,
    enabled: !!tenantId,
    refetchInterval: 30_000,
  })

  const s = useSessoesSsm(tenantId, tenant?.name ?? '')

  // O que falta para entrar com o proprio usuario do LDAP. So o tenant aberto conta:
  // fora do shell pessoal a sessao nem usa o usuario da pessoa.
  const { data: acesso } = useAcessoServidores()
  const avisosDoAcesso = avisosDeAcesso(acesso, { tenantId })

  const filtradas = useMemo(
    () => filtrarInstancias(data?.instancias ?? [], busca),
    [data, busca],
  )

  if (!tenantId) {
    return (
      <Alert color="blue" title="Escolha um tenant">
        Selecione a organização e o tenant na barra lateral para ver os servidores.
      </Alert>
    )
  }

  const ocultos = data ? explicarOcultos(data.excluidas) : []
  const aviso = data ? avisoDeCobertura(data) : null
  const abaAtiva = s.abas.find(a => a.instanceId === s.ativa)
  const noTeto = contagem ? contagem.abertas >= contagem.teto : false

  // Em estreito, mostra UMA coisa por vez.
  const mostrarLista = !estreito || !s.ativa

  // Recolhida: uma faixa com o botao de voltar e o contador. Nao viro a lista
  // em coluna de icones — com 35 servidores, 35 icones iguais nao ajudam a
  // escolher, e quem ja tem sessao aberta navega pelas ABAS.
  const faixaRecolhida = (
    <Box
      w={44}
      style={{
        borderRight: '1px solid var(--color-border-light)',
        display: 'flex', flexDirection: 'column', alignItems: 'center',
        gap: 8, paddingTop: 12, flexShrink: 0,
      }}
    >
      <Tooltip label="Mostrar a lista de servidores" position="right">
        <UnstyledButton
          onClick={() => setRecolhida(false)}
          aria-label="Mostrar a lista de servidores"
          style={{ display: 'flex', color: 'var(--color-text-secondary)' }}
        >
          <IconLayoutSidebarLeftExpand size={18} />
        </UnstyledButton>
      </Tooltip>
      {contagem && (
        <Tooltip label="Sessões abertas / limite por pessoa" position="right">
          <Badge size="sm" variant="light" color={noTeto ? 'red' : 'gray'}>
            {contagem.abertas}
          </Badge>
        </Tooltip>
      )}
    </Box>
  )
  const mostrarTerminal = !estreito || !!s.ativa

  const lista = (
    <Box
      w={estreito ? '100%' : 280}
      style={{
        borderRight: estreito ? undefined : '1px solid var(--color-border-light)',
        display: 'flex', flexDirection: 'column', minHeight: 0,
      }}
    >
      <Box p="sm" pb={6}>
        <Group justify="space-between" align="center" wrap="nowrap">
          <Group gap={4} wrap="nowrap">
            {!estreito && (
              <Tooltip label="Recolher a lista e dar a largura ao terminal">
                <UnstyledButton
                  onClick={() => setRecolhida(true)}
                  aria-label="Recolher a lista de servidores"
                  style={{ display: 'flex', color: 'var(--color-text-secondary)' }}
                >
                  <IconLayoutSidebarLeftCollapse size={16} />
                </UnstyledButton>
              </Tooltip>
            )}
            <Text fw={600} size="sm" c="var(--ai-brand-azul, #025468)">Servidores</Text>
          </Group>
          {contagem && (
            <Tooltip
              label={
                noTeto
                  ? 'Você atingiu o limite. Feche uma sessão para abrir outra.'
                  : 'Sessões abertas / limite por pessoa'
              }
            >
              <Badge size="sm" variant="light" color={noTeto ? 'red' : 'gray'}>
                {contagem.abertas}/{contagem.teto}
              </Badge>
            </Tooltip>
          )}
        </Group>
        <Text size="xs" c="dimmed">{tenant?.name}</Text>
      </Box>

      {/* Busca: a conta central tem 35 servidores, e rolar era o único
          caminho para achar um. */}
      <Box px="xs" pb={6}>
        <TextInput
          size="xs"
          placeholder="Buscar por nome, id ou ambiente…"
          leftSection={<IconSearch size={13} />}
          value={busca}
          onChange={e => setBusca(e.currentTarget.value)}
        />
      </Box>

      <ScrollArea style={{ flex: 1, minHeight: 0 }}>
        <Stack gap={2} px="xs" pb="sm">
          {isLoading && <Loader size="sm" m="md" />}

          {error && (
            <Alert color="red" title="Não consegui listar" m="xs">
              <Text size="xs">{mensagemDoErro(error)}</Text>
            </Alert>
          )}

          {filtradas.map((i: InstanciaSsm) => {
            const aberta = s.abas.find(a => a.instanceId === i.instance_id)
            const alcancavel = estaAlcancavel(i)
            const bloqueado = !alcancavel || (noTeto && !aberta)
            return (
              <Tooltip
                key={i.instance_id}
                disabled={!bloqueado}
                label={
                  !alcancavel
                    ? `O agente SSM deste servidor está ${i.ping ?? 'indisponível'} — a sessão não vai abrir.`
                    : 'Você atingiu o limite de sessões. Feche uma para abrir esta.'
                }
              >
                <UnstyledButton
                  onClick={() =>
                    !bloqueado &&
                    s.abrir(i.instance_id, i.nome ?? i.instance_id, i.environment)
                  }
                  p="xs"
                  style={{
                    borderRadius: 6,
                    // Bloqueado fica visível e opaco, não escondido: sumir da
                    // lista faria a pessoa achar que o servidor não existe.
                    opacity: bloqueado ? 0.45 : 1,
                    cursor: bloqueado ? 'not-allowed' : 'pointer',
                    // `--color-bg-selected`, nao `gray-1`: a escala cinza do
                    // Mantine NAO inverte no escuro (gray-1 resolve para #f1f3f5
                    // em ambos), e o item selecionado virava bloco branco.
                    background: s.ativa === i.instance_id
                      ? 'var(--color-bg-selected)' : undefined,
                    borderLeft: aberta
                      ? `3px solid var(--mantine-color-${COR_DO_ESTADO[aberta.estado]}-6)`
                      : '3px solid transparent',
                  }}
                >
                  <Group gap={6} wrap="nowrap">
                    {alcancavel
                      ? <IconServer size={14} />
                      : <IconPlugConnectedX size={14} color="var(--mantine-color-orange-6)" />}
                    <Box style={{ minWidth: 0, flex: 1 }}>
                      <Text size="sm" truncate>{i.nome ?? i.instance_id}</Text>
                      {/* Terciario pelo PESO e pela cor, nunca pelo tamanho.
                          Cheguei a por 10px aqui e a medicao reprovou: 3,10 no
                          escuro e 2,93 no claro, contra os 4,5 do DS 11.1. O id
                          e o que se confere antes de abrir shell num servidor —
                          informacao essencial, e o DS 11.2 so libera o cinza
                          fraco para decorativo.

                          Com `--color-text-secondary`: 8,01 e 10,39 no
                          escuro (inalterados). No claro ERA 4,13 e 4,22 —
                          0,3 abaixo do limiar, a MESMA divida de design
                          system registrada para a tabela densa. RESOLVIDO em
                          22/09/2026 (TASK-11185): o token foi retintado
                          (`#6c757d` -> `#5c636a`), e fecha 5,37 e 5,48 agora.
                          Subir para o primario continua sendo a alternativa
                          que mataria a hierarquia que este texto existe para
                          criar — nao foi preciso. */}
                      <Text size="xs" ff="monospace" truncate
                            c="var(--color-text-secondary)"
                            style={{ lineHeight: 1.3 }}>
                        {i.instance_id}
                      </Text>
                    </Box>
                    {/* `light`, nao solido: 17 blocos vermelhos saturados numa
                        conta so de producao viram ruido, e o ambiente deixa de
                        discriminar justamente onde tudo e PRD.
                        A VARIANTE depende do esquema, e isso foi MEDIDO contra
                        o DS 11.1 (4,5:1):

                          escuro, `light`  -> 7,54 a 9,59  (passa)
                          claro,  `light`  -> 2,47 a 3,36  (reprova)
                          claro,  `filled` base -> 3,04 a 4,20  (reprova, e
                                     esse era o estado ANTERIOR a esta revisao)
                          claro,  `filled` tom 8  -> DEV 11,51 / PRD 4,51 /
                                     QA 5,02 / HML 3,58

                        DIVIDA DECLARADA: o HML fica em 3,58. Laranja com texto
                        branco nao alcanca 4,5 em tom nenhum usavel, e o
                        `autoContrast` do Mantine nao troca o texto nesse tom
                        (testado: nao mudou uma casa decimal). Trocar o HUE do
                        HML e decisao de design system, nao desta tela.

                        Tentei `light` com tom `.9` no claro e ficou PIOR
                        (2,03 a 2,79): a variante deriva fundo E texto do mesmo
                        tom, entao escurecer o texto escurece o fundo junto. */}
                    <Badge
                      size="xs"
                      variant={esquema === 'dark' ? 'light' : 'filled'}
                      color={
                        esquema === 'dark'
                          ? (COR_DO_AMBIENTE[i.environment] ?? 'gray')
                          : `${COR_DO_AMBIENTE[i.environment] ?? 'gray'}.8`
                      }
                    >
                      {i.environment}
                    </Badge>
                  </Group>
                </UnstyledButton>
              </Tooltip>
            )
          })}

          {data && data.instancias.length === 0 && (
            <Text size="xs" c="dimmed" p="sm">
              Nenhum servidor que você possa acessar neste tenant.
            </Text>
          )}

          {data && data.instancias.length > 0 && filtradas.length === 0 && (
            <Text size="xs" c="dimmed" p="sm">
              Nenhum servidor casa com “{busca}”. Os {data.instancias.length} da
              lista continuam lá — limpe a busca para vê-los.
            </Text>
          )}
        </Stack>
      </ScrollArea>

      {/* Os OCULTOS aparecem, com o motivo. "A conta tem 3 servidores" e "a
          conta tem 12 e você vê 3" são coisas diferentes, e a segunda é a
          verdadeira. O motivo mais comum não tem nada a ver com permissão. */}
      {ocultos.length > 0 && (
        <Box p="xs" style={{ borderTop: '1px solid var(--color-border-light)' }}>
          <Group gap={4} mb={4}>
            <IconEyeOff size={13} />
            <Text size="xs" fw={600}>Não aparecem aqui</Text>
          </Group>
          {ocultos.map(o => (
            <Text key={o.motivo} size="xs" c="dimmed">
              {o.quantos} {o.quantos === 1 ? 'servidor' : 'servidores'} — {o.motivo}
            </Text>
          ))}
        </Box>
      )}

      {aviso && (
        <Alert color="yellow" icon={<IconAlertTriangle size={14} />} p="xs" m="xs">
          <Text size="xs">{aviso}</Text>
        </Alert>
      )}
    </Box>
  )

  const painel = (
    <Box style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0, minHeight: 0 }}>
      {/* As sessoes pinadas NAO aparecem como aba — dois renderizadores da mesma
          sessao brigariam pelo terminal. Mas sem esta faixa a pessoa pina duas,
          navega, volta para ca e ve zero abas, com as janelas atras do navegador:
          "perdi minhas sessoes" vira chamado. */}
      {s.pinadas.length > 0 && (
        <Group
          gap={6} px={10} py={4}
          style={{ borderBottom: '1px solid var(--color-border-light)' }}
        >
          <IconPin size={12} />
          <Text size="xs" c="dimmed">
            {s.pinadas.length} {s.pinadas.length === 1 ? 'pinada' : 'pinadas'} em
            janela — continuam abertas em qualquer tela
          </Text>
          <Button
            size="compact-xs"
            variant="subtle"
            onClick={() => s.pinadas.forEach(x => despinar(x.instanceId))}
          >
            Trazer para abas
          </Button>
        </Group>
      )}
      {(s.abas.length > 0 || (estreito && s.ativa)) && (
        <Group
          gap={4} px={6} pt={6} align="flex-end"
          style={{ borderBottom: '1px solid var(--color-border-light)' }}
        >
          {estreito && (
            <UnstyledButton onClick={() => s.setAtiva(null)} px="xs">
              <Text size="xs" c="dimmed">← Servidores</Text>
            </UnstyledButton>
          )}
          {s.abas.map(a => (
            <Group
              key={a.instanceId}
              gap={4} px="xs" py={6}
              style={{
                cursor: 'pointer',
                // A ativa recebe o fundo DO TERMINAL e desce 1px para cobrir a
                // borda da barra: o olho le "esta aba E aquele painel", em vez
                // de um retangulo solto por cima de outro. Antes era `gray-2`,
                // que no escuro resolve para #e9ecef — bloco branco com o
                // rotulo claro por cima, ilegivel.
                borderRadius: s.ativa === a.instanceId ? '6px 6px 0 0' : 6,
                marginBottom: s.ativa === a.instanceId ? -1 : 0,
                background: s.ativa === a.instanceId
                  ? FUNDO_DO_TERMINAL : undefined,
                color: s.ativa === a.instanceId ? '#e6f3f5' : undefined,
              }}
              onClick={() => s.setAtiva(a.instanceId)}
            >
              <Badge size="xs" circle color={COR_DO_ESTADO[a.estado]}> </Badge>
              <Text size="xs" c="inherit">{a.nome}</Text>
              {/* Pinar so em tela larga: abaixo de 62em a tela ja mostra lista
                  OU terminal, um de cada vez, porque os dois juntos deixam o
                  terminal inutilizavel — e janela arrastavel nessa largura e
                  pior ainda. */}
              {!estreito && (
                <UnstyledButton
                  aria-label={`Pinar ${a.nome}`}
                  title="Pinar numa janela que continua aberta em qualquer tela"
                  onClick={e => { e.stopPropagation(); pinar(a.instanceId) }}
                >
                  <IconPin size={12} />
                </UnstyledButton>
              )}
              <UnstyledButton
                aria-label={`Fechar ${a.nome}`}
                onClick={e => { e.stopPropagation(); s.fechar(a.instanceId) }}
              >
                <IconX size={12} />
              </UnstyledButton>
            </Group>
          ))}
        </Group>
      )}

      {abaAtiva?.motivo && abaAtiva.estado !== 'aberta' && (
        <Alert
          color={abaAtiva.estado === 'recusada' ? 'red' : 'gray'}
          m="xs"
          title={abaAtiva.estado === 'recusada' ? 'Sessão recusada' : 'Sessão encerrada'}
        >
          <Text size="sm">{abaAtiva.motivo.mensagem}</Text>
          {abaAtiva.motivo.vaiAdiantarTentarDeNovo && (
            <Text size="xs" c="dimmed" mt={4}>Tentar de novo pode resolver.</Text>
          )}
        </Alert>
      )}

      {/* Deriva de `abaAtiva`, e NAO de `s.ativa` cru: uma sessao pinada e
          desenhada pela janela flutuante, e desenhar aqui tambem faria dois
          terminais vivos para a mesma sessao. Guarda estrutural — vale mesmo se
          algum outro caminho deixar `ativa` apontando para uma flutuante. */}
      <Box style={{ flex: 1, minHeight: 0, background: abaAtiva ? FUNDO_DO_TERMINAL : undefined }}>
        {abaAtiva ? (
          <TerminalDoServidor
            key={abaAtiva.instanceId}
            instanceId={abaAtiva.instanceId}
            historico={s.historico}
            ouvir={s.ouvir}
            digitar={s.digitar}
            redimensionar={s.redimensionar}
          />
        ) : (
          // O vazio DIZ ALGO em vez de ocupar meia tela com uma frase: o que
          // ajuda antes de escolher é saber o que está aberto e o que a lista
          // não mostra.
          <Stack gap={6} p="lg" maw={520}>
            <Text size="sm" fw={600}>
              {recolhida && !estreito
                ? 'A lista está recolhida'
                : 'Escolha um servidor à esquerda'}
            </Text>
            <Text size="xs" c="dimmed">
              {recolhida && !estreito
                ? 'Abra a lista no botão da faixa à esquerda para escolher um servidor.'
                : 'O terminal abre ao lado. Dá para deixar vários abertos ao mesmo '
                  + 'tempo — trocar de aba não encerra a sessão anterior.'}
            </Text>
            {contagem && (
              <Text size="xs" c={noTeto ? 'red' : 'dimmed'}>
                {contagem.abertas} de {contagem.teto} sessões em uso
                {noTeto && ' — feche uma para abrir outra'}.
              </Text>
            )}
            <Text size="xs" c="dimmed">
              Tudo que for digitado e devolvido fica gravado. Se a gravação não
              estiver disponível, a sessão não abre.
            </Text>
          </Stack>
        )}
      </Box>
    </Box>
  )

  const grade = (
    <Group
      align="stretch"
      gap={0}
      wrap="nowrap"
      // `flex: 1` + `minHeight: 0` em vez de `calc(100vh - 120px)`: aquele 120
      // era número mágico e quebrava em silêncio se o cabeçalho mudasse de
      // altura.
      style={{ flex: 1, minHeight: 0, height: '100%' }}
    >
      {mostrarLista && (recolhida && !estreito ? faixaRecolhida : lista)}
      {mostrarTerminal && painel}
    </Group>
  )

  if (avisosDoAcesso.length === 0) return grade

  // Acima da grade, e nao dentro do terminal: o aviso vale antes de abrir a sessao
  // (a de grupo, sobretudo — sem grupo o servidor recusa a entrada).
  return (
    <Stack gap={0} style={{ flex: 1, minHeight: 0, height: '100%' }}>
      {avisosDoAcesso.map((a) => (
        <Alert
          key={a.tipo}
          color={a.gravidade === 'danger' ? 'red' : 'yellow'}
          icon={<IconAlertTriangle size={16} />}
          mb="xs"
        >
          <Group gap="sm" justify="space-between" wrap="nowrap">
            <Text size="sm">{a.texto}</Text>
            {a.acao && (
              <Button component={Link} to={a.acao.para} size="xs" variant="light" color="yellow">
                {a.acao.rotulo}
              </Button>
            )}
          </Group>
        </Alert>
      ))}
      <Box style={{ flex: 1, minHeight: 0, display: 'flex' }}>{grade}</Box>
    </Stack>
  )
}

/** Traduz o erro da listagem — dizer "erro 409" manda procurar no lugar errado. */
function mensagemDoErro(e: unknown): string {
  const status = (e as { response?: { status?: number } })?.response?.status
  const detalhe = String(
    (e as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail ?? '',
  )
  if (status === 409 && detalhe.includes('tenant_sem_account_id')) {
    return 'Este tenant ainda não tem conta AWS associada — não há servidores para listar.'
  }
  if (status === 503) {
    return 'Não consegui assumir a role deste tenant na AWS. É a role/trust do tenant, não a sua permissão.'
  }
  if (status === 403) return 'Você não tem permissão para listar servidores neste tenant.'
  return `Falha na consulta${status ? ` (HTTP ${status})` : ''}.`
}
